import { useEffect, useEffectEvent } from "react"

import { formatDateTime } from "@/helpers/dateFormat"

import { useCacheContext } from "./cache-context"

/**
 * Type representing data that contains cache information
 *
 * We use `unknown` instead of `any` for type safety - it forces us to use type guards
 * to safely access nested properties. The expected structure is:
 *
 * ```typescript
 * {
 *   data?: {
 *     cacheDetails?: {
 *       cacheSetTimestamp?: string
 *     }
 *   }
 * }
 * ```
 */
type CacheableData = unknown

const extractTimestamp = (data: CacheableData): string | undefined => {
  // Type guard to safely access nested properties
  if (
    data &&
    typeof data === "object" &&
    "data" in data &&
    data.data &&
    typeof data.data === "object" &&
    "cacheDetails" in data.data &&
    data.data.cacheDetails &&
    typeof data.data.cacheDetails === "object" &&
    "cacheSetTimestamp" in data.data.cacheDetails &&
    typeof data.data.cacheDetails.cacheSetTimestamp === "string"
  ) {
    return formatDateTime(data.data.cacheDetails.cacheSetTimestamp)
  }

  return undefined
}

/**
 * Custom hook to automatically update cache timestamps when data loads
 * and reset cache when query parameters change
 *
 * @param dataOrDataArray - Single data object or array of data objects with cache details
 * @param queryDependencies - Dependencies that should trigger cache reset (e.g., search params)
 */
export function useCacheUpdater(
  dataOrDataArray: CacheableData | CacheableData[],
  isLoading: boolean,
) {
  const {
    resetCacheTimestamp,
    updateCacheTimestamp,
    updateMultipleCacheTimestamps,
  } = useCacheContext()

  const onLoadingChange = useEffectEvent(() => {
    resetCacheTimestamp()
  })

  const onDataChange = useEffectEvent(() => {
    if (Array.isArray(dataOrDataArray)) {
      const timestamps = dataOrDataArray
        .map(extractTimestamp)
        .filter((timestamp): timestamp is string => timestamp !== undefined)

      if (timestamps.length > 0) {
        updateMultipleCacheTimestamps(timestamps)
      }
    } else {
      const timestamp = extractTimestamp(dataOrDataArray)
      if (timestamp) {
        updateCacheTimestamp(timestamp)
      }
    }
  })

  // Reset cache when query parameters change (new query is starting)
  useEffect(() => {
    if (isLoading) {
      onLoadingChange()
    }
  }, [isLoading])

  // Update cache when data loads successfully
  useEffect(() => {
    onDataChange()
  }, [dataOrDataArray])
}
